/**
 * Hooks para TEN-02/03: Panel de Super Administrador
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Types ────────────────────────────────────────────────────────────────────

// `nfr` es la licencia del contrato de partners: un tenant PRODUCTIVO —emite
// documentos tributarios de verdad— entregado gratis mientras dure el contrato,
// para el uso interno del partner. El tenant de demostración del mismo contrato
// es `demo`, que va contra el ambiente de pruebas de la autoridad o el simulador.
export type AccountType = 'production' | 'trial' | 'demo' | 'nfr'

export interface PlatformTenant {
  id: number
  schema_name: string
  name: string
  tax_id: string
  country: string
  country_display: string
  status: string
  status_display: string
  account_type: AccountType
  account_type_display: string
  licensed_users: number
  max_users: number
  allow_homologation: boolean
  /** Override del tope de trabajadores. null = derivado por tramos. */
  max_employees: number | null
  /** Tope efectivo de trabajadores activos (override o tramo). */
  employee_limit: number
  active_users: number
  /** Cupos de cajero del POS (licencia que solo opera el terminal) y cuántos se usan. */
  licensed_pos_users: number
  active_pos_users: number
  monthly_cost_usd: number | null
  created_at: string
  trial_ends_at: string | null
  /** Días que le quedan a una cuenta temporal. null = no caduca. */
  days_until_expiry: number | null
  /** Motivo del último aprovisionamiento fallido; vacío si terminó bien. */
  provisioning_error: string
}

export interface PlatformTenantsResponse {
  tenants: PlatformTenant[]
  count: number
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const platformKeys = {
  tenants: (params?: object) => ['platform', 'tenants', params] as const,
}

// ─── TEN-02: Tenants ──────────────────────────────────────────────────────────

export function usePlatformTenants(params?: { status?: string; country?: string }) {
  return useQuery<PlatformTenantsResponse>({
    queryKey: platformKeys.tenants(params),
    queryFn: async () => {
      const res = await apiClient.get('/platform/tenants/', { params })
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useUpdateTenant(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name?: string; tax_id?: string; licensed_users?: number; max_users?: number; allow_homologation?: boolean; max_employees?: number | null; licensed_pos_users?: number }) => {
      const res = await apiClient.patch(`/platform/tenants/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })
}

export function useConvertTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, licensed_users }: { id: number; licensed_users: number }) => {
      const res = await apiClient.post(`/platform/tenants/${id}/convert/`, { licensed_users })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })
}

export function useExtendTrial() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, days }: { id: number; days: number }) => {
      const res = await apiClient.post(`/platform/tenants/${id}/extend-trial/`, { days })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })
}

export function useToggleTenantStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'activate' | 'deactivate' }) => {
      const res = await apiClient.post(`/platform/tenants/${id}/toggle-status/`, { action })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })
}

/** Vuelve a encolar el aprovisionamiento de un tenant fallido o colgado. */
export function useRetryProvisioning() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.post(`/platform/tenants/${id}/retry-provisioning/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] })
    },
  })
}

// ─── TEN-04: Tenant Localization ──────────────────────────────────────────────

export interface TenantLocalization {
  configured: boolean
  tenant_id: number
  tenant_name: string
  country?: string
  id?: number
  country_code?: string
  country_display?: string
  currency?: string
  currency_display?: string
  tax_name?: string
  tax_rate?: string
  is_configured?: boolean
  sii_environment?: string
  resolucion_number?: string
  resolucion_date?: string
  rut_envia?: string
}

export function useTenantLocalization(tenantId: number | null) {
  return useQuery<TenantLocalization>({
    queryKey: ['platform', 'tenant-localization', tenantId],
    queryFn: async () => {
      const res = await apiClient.get(`/platform/tenants/${tenantId}/localization/`)
      return res.data
    },
    enabled: !!tenantId,
  })
}

export function useUpdateTenantLocalization(tenantId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.put(`/platform/tenants/${tenantId}/localization/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenant-localization', tenantId] })
    },
  })
}

// ─── Contribuyentes Electrónicos ──────────────────────────────────────────────

export interface ElectronicTaxpayer {
  id: number
  tax_id: string
  environment: string
  environment_display: string
  legal_name: string
  resolution_number: string
  resolution_date: string
  email_intercambio: string
  url: string
  updated_at: string
}

interface ElectronicTaxpayerListResponse {
  count: number
  page: number
  page_size: number
  last_updated: string | null
  taxpayers: ElectronicTaxpayer[]
  /** Criterio de búsqueda del padrón. Solo por RUT: indexar razón social y
   *  correo costaba 227 MB para una búsqueda con 0 usos en 42 días. */
  search_by?: 'tax_id'
  /** true cuando el término no parecía un RUT y no se aplicó filtro. */
  search_ignored?: boolean
}

interface CEStatsResponse {
  total: number
  last_updated: string | null
  by_environment: Record<string, number>
}

export function useElectronicTaxpayers(params: {
  search?: string
  environment?: string
  page?: number
  page_size?: number
}) {
  return useQuery<ElectronicTaxpayerListResponse>({
    queryKey: ['electronic-taxpayers', params],
    queryFn: async () => {
      const res = await apiClient.get('/platform/electronic-taxpayers/', { params })
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useElectronicTaxpayerStats() {
  return useQuery<CEStatsResponse>({
    queryKey: ['electronic-taxpayers-stats'],
    queryFn: async () => {
      const res = await apiClient.get('/platform/electronic-taxpayers/stats/')
      return res.data
    },
    staleTime: 10 * 60 * 1000,
  })
}

export function useTriggerCEDownload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post('/platform/electronic-taxpayers/').then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['electronic-taxpayers'] })
      qc.invalidateQueries({ queryKey: ['electronic-taxpayers-stats'] })
    },
  })
}

// ─── Seasonal Themes (platform admin CRUD) ───────────────────────────────────

export interface SeasonalThemeDto {
  id: string
  name: string
  country: string
  start_month: number
  start_day: number
  end_month: number
  end_day: number
  priority: number
  emoji: string
  message: string
  gradient_light: string
  gradient_dark: string
  text_color: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export const seasonalKeys = {
  all: ['platform', 'seasonal-themes'] as const,
  active: ['seasonal-theme-active'] as const,
}

export function useSeasonalThemes() {
  return useQuery<{ themes: SeasonalThemeDto[] }>({
    queryKey: seasonalKeys.all,
    queryFn: () => apiClient.get('/platform/seasonal-themes/').then(r => r.data),
    staleTime: 30_000,
  })
}

export function useCreateSeasonalTheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<Partial<SeasonalThemeDto>, 'id' | 'created_at' | 'updated_at'>) =>
      apiClient.post('/platform/seasonal-themes/', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: seasonalKeys.all })
      qc.invalidateQueries({ queryKey: seasonalKeys.active })
    },
  })
}

export function useUpdateSeasonalTheme(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<SeasonalThemeDto>) =>
      apiClient.patch(`/platform/seasonal-themes/${id}/`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: seasonalKeys.all })
      qc.invalidateQueries({ queryKey: seasonalKeys.active })
    },
  })
}

export function useDeleteSeasonalTheme() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/platform/seasonal-themes/${id}/`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: seasonalKeys.all })
      qc.invalidateQueries({ queryKey: seasonalKeys.active })
    },
  })
}

/**
 * Retorna el theme activo para el tenant+fecha (o un preview si preview!=null).
 * Cualquier usuario autenticado puede consultar.
 */
export function useActiveSeasonalTheme(preview?: string | null) {
  return useQuery<{ active: SeasonalThemeDto | null }>({
    queryKey: [...seasonalKeys.active, preview ?? null] as const,
    queryFn: () => {
      const params = preview ? { preview } : undefined
      return apiClient.get('/config/seasonal-themes/active/', { params }).then(r => r.data)
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

// ─── Ventanas de mantenimiento ────────────────────────────────────────────────

export type MaintenanceState = 'scheduled' | 'imminent' | 'in_progress'
export type MaintenanceSeverity = 'info' | 'warning' | 'critical'

export interface MaintenanceWindowDto {
  id: string
  title: string
  message: string
  severity: MaintenanceSeverity
  starts_at: string
  ends_at: string
  /** Lo resuelve el BACKEND: el reloj del navegador no decide si hay mantenimiento en curso. */
  state: MaintenanceState
  can_dismiss: boolean
}

export interface MaintenanceWindowAdminDto extends Omit<MaintenanceWindowDto, 'state' | 'can_dismiss'> {
  severity_label: string
  show_from: string
  is_active: boolean
  dismissible: boolean
  state: MaintenanceState | null
  created_at: string
  updated_at: string
}

export const maintenanceKeys = {
  active: ['maintenance-window', 'active'] as const,
  list: ['maintenance-windows'] as const,
}

/** El aviso vigente para el banner. Lo consume cualquier usuario autenticado. */
export function useActiveMaintenanceWindow() {
  return useQuery<{ window: MaintenanceWindowDto | null; server_time?: string }>({
    queryKey: maintenanceKeys.active,
    queryFn: () => apiClient.get('/config/maintenance-window/active/').then(r => r.data),
    // Se refresca cada 5 min: el aviso puede publicarse o pasar a "en curso"
    // mientras el usuario tiene la pestaña abierta, y ese es justo el momento en
    // que más importa que lo vea.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  })
}

export function useMaintenanceWindows() {
  return useQuery<{ windows: MaintenanceWindowAdminDto[] }>({
    queryKey: maintenanceKeys.list,
    queryFn: () => apiClient.get('/platform/maintenance-windows/').then(r => r.data),
  })
}

export function useCreateMaintenanceWindow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<MaintenanceWindowAdminDto>) =>
      apiClient.post('/platform/maintenance-windows/', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.list })
      qc.invalidateQueries({ queryKey: maintenanceKeys.active })
    },
  })
}

export function useUpdateMaintenanceWindow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MaintenanceWindowAdminDto> }) =>
      apiClient.patch(`/platform/maintenance-windows/${id}/`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.list })
      qc.invalidateQueries({ queryKey: maintenanceKeys.active })
    },
  })
}

export function useDeleteMaintenanceWindow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/platform/maintenance-windows/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.list })
      qc.invalidateQueries({ queryKey: maintenanceKeys.active })
    },
  })
}

// ─── Aviso de demora del SII ──────────────────────────────────────────────────

export interface SiiNoticeDto {
  message: string
  since: string | null
  /** `auto`: lo encendió la vigilancia; `manual`: lo forzó el platform admin. */
  source: 'auto' | 'manual'
}

export type SiiNoticeMode = 'auto' | 'forced_on' | 'forced_off'

export interface SiiServiceStatusDto {
  mode: SiiNoticeMode
  message: string
  default_message: string
  is_slow: boolean
  slow_since: string | null
  waiting_documents: number
  affected_taxpayers: number
  stalled_documents: number
  sii_unreachable: boolean
  checked_at: string | null
  alert_sent_at: string | null
  updated_at: string
}

export const siiStatusKeys = {
  notice: ['sii-status', 'notice'] as const,
  admin: ['sii-status', 'admin'] as const,
}

/** El aviso de demora del SII para este tenant, o `null`. Lo decide el backend. */
export function useSiiNotice() {
  return useQuery<{ notice: SiiNoticeDto | null }>({
    queryKey: siiStatusKeys.notice,
    queryFn: () => apiClient.get('/config/sii-status/').then(r => r.data),
    // La vigilancia mide cada 5 min; 2 min basta para que el aviso aparezca
    // mientras el usuario mira un documento «Consultando».
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    retry: 1,
  })
}

export function useSiiServiceStatus() {
  return useQuery<SiiServiceStatusDto>({
    queryKey: siiStatusKeys.admin,
    queryFn: () => apiClient.get('/platform/sii-status/').then(r => r.data),
    refetchInterval: 60 * 1000,
  })
}

export function useUpdateSiiServiceStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Pick<SiiServiceStatusDto, 'mode' | 'message'>>) =>
      apiClient.patch('/platform/sii-status/', data).then(r => r.data as SiiServiceStatusDto),
    onSuccess: (data) => {
      qc.setQueryData(siiStatusKeys.admin, data)
      qc.invalidateQueries({ queryKey: siiStatusKeys.notice })
    },
  })
}
